import React, { useEffect, useMemo, useState } from "react";

import { api, apiErrorMessage } from "@/lib/api";
import { uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Search, ExternalLink, Eye, Flame, Lock, PlayCircle, Star, Pencil, Trash2, Trophy, Upload, Zap, AlertCircle, CheckCircle2, BadgeCheck, Megaphone, Sparkles, RefreshCcw, Plus, X as XIcon, CreditCard, DollarSign, Send, Info, Film, Users, Wallet, GripVertical, Share2, Copy as CopyIcon } from "lucide-react";
import { AreaChart, Area, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { LikeButton } from "@/components/campaign/LikeButton";
import { CopyLinkButton } from "@/components/campaign/CopyLinkButton";
import { PlatformIcon, PlatformPill } from "@/components/campaign/PlatformIcon";
import { PaymentMethodIcon, PAYMENT_METHODS, PaymentMethodChip } from "@/components/campaign/PaymentMethodIcon";
import { AppealDialog } from "@/components/clip/AppealDialog";
import {
  NewBannerCard,
  NewDescriptionCard,
  NewReferencesCard,
  NewMusicBrandingCard,
  NewRulesFooter,
  NewStatsPanel,
  NewLeaderboardList,
  NewTabsBar,
} from "@/components/campaign/CampaignDetailLayout";

function fmtNumber(n) {
  return Number(n || 0).toLocaleString("en-US");
}

function isCampaignNew(c) {
  if (!c?.created_at || c?.show_new_badge === false) return false;
  const created = new Date(c.created_at).getTime();
  return Date.now() - created < 7 * 24 * 60 * 60 * 1000;
}

function isCampaignUpdated(c) {
  if (!c?.created_at || !c?.updated_at || c?.show_updated_badge === false) return false;
  const created = new Date(c.created_at).getTime();
  const updated = new Date(c.updated_at).getTime();
  if (updated - created < 60 * 1000) return false; // not really updated
  return Date.now() - updated < 3 * 24 * 60 * 60 * 1000 && !isCampaignNew(c);
}



const STATUSES = ["active", "paused", "ended"];

const PLATFORMS = ["TikTok", "Instagram", "YouTube"];

const PLATFORM_ICONS = {
  TikTok: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23ff0050' d='M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.72a8.19 8.19 0 004.76 1.52V6.79a4.83 4.83 0 01-1-.1z'/%3E%3C/svg%3E",
  Instagram:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Instagram_icon.png/120px-Instagram_icon.png",
  YouTube: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/YouTube_full-color_icon_%282017%29.svg/120px-YouTube_full-color_icon_%282017%29.svg.png",
  X: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'/%3E%3C/svg%3E",
  Snapchat: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23FFFC00'%3E%3Cpath d='M12.166 1.001c.292.013.59.041.881.083 1.793.255 3.252 1.272 4.097 2.86.527.987.756 2.07.793 3.182.024.706.024 1.412.005 2.119-.005.156.041.241.176.32.339.197.706.314 1.107.345.387.029.748-.022 1.075-.235.318-.208.704.024.69.402-.014.42-.252.683-.587.876-.55.317-1.154.467-1.764.617-.236.058-.276.143-.222.374.222.946.726 1.748 1.41 2.43.683.682 1.488 1.205 2.39 1.581.226.094.476.156.71.235.4.135.522.453.346.836-.187.405-.494.704-.879.93-.65.38-1.366.6-2.107.722-.131.022-.21.075-.255.198-.118.318-.348.508-.673.605-.467.14-.948.157-1.43.13-.45-.025-.898-.045-1.345-.013-.55.04-1.075.182-1.5.564-.426.382-.86.745-1.342 1.05-.755.476-1.582.703-2.479.703-.897 0-1.724-.227-2.479-.703-.482-.305-.916-.668-1.342-1.05-.425-.382-.95-.524-1.5-.564-.447-.032-.895-.012-1.345.013-.482.027-.963.01-1.43-.13-.325-.097-.555-.287-.673-.605-.045-.123-.124-.176-.255-.198-.741-.122-1.457-.342-2.107-.722-.385-.226-.692-.525-.879-.93-.176-.383-.054-.701.346-.836.234-.079.484-.141.71-.235.902-.376 1.707-.899 2.39-1.58.684-.683 1.188-1.485 1.41-2.431.054-.231.014-.316-.222-.374-.61-.15-1.214-.3-1.764-.617-.335-.193-.573-.456-.587-.876-.014-.378.372-.61.69-.402.327.213.688.264 1.075.235.401-.031.768-.148 1.107-.345.135-.079.181-.164.176-.32-.019-.707-.019-1.413.005-2.119.037-1.112.266-2.195.793-3.182C8.255 2.356 9.714 1.339 11.507 1.084c.218-.031.438-.055.659-.083z'/%3E%3C/svg%3E",
};

const CATEGORIES = ["Gaming", "Entertainment", "Music", "IRL", "Finance", "Podcasts", "Memes", "Lifestyle", "Other"];
const CAMPAIGN_TYPES = ["Logo", "Clipping", "Music", "Movies"];

function compactNumber(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return n.toLocaleString();
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    available: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    paused: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    ended: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    full: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  };
  const dot = {
    active: "bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]",
    available: "bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]",
    paused: "bg-amber-500 opacity-60",
    ended: "bg-rose-500 opacity-50",
    full: "bg-rose-500 opacity-50",
  };
  return (
    <span
      data-testid={`campaign-status-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${map[status] || "bg-muted text-muted-foreground"}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot[status] || "bg-muted-foreground"}`} />
      {status}
    </span>
  );
}

function CampaignForm({ initial, onSave, saving }) {
  const { me } = useAuth();
  const [name, setName] = useState(initial?.name || "");
  const [creatorBrand, setCreatorBrand] = useState(initial?.creator_brand || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [descriptionFormat, setDescriptionFormat] = useState(initial?.description_format || "plain");
  const [platforms, setPlatforms] = useState(initial?.platforms_allowed || []);
  const [payout, setPayout] = useState(String(initial?.payout_cpm ?? 0));
  const [threshold, setThreshold] = useState(String(initial?.payout_threshold ?? 0));
  const [targetViews, setTargetViews] = useState(String(initial?.target_views ?? 0));
  const [status, setStatus] = useState(initial?.status || "active");
  const [bannerUrl, setBannerUrl] = useState(initial?.banner_url || "");
  const [bannerDataUrl, setBannerDataUrl] = useState(initial?.banner_image_data_url || "");
  const [cardColor, setCardColor] = useState(initial?.card_color || "");
  const [progressColor, setProgressColor] = useState(initial?.progress_color || "#c8ff00");
  const [minViews, setMinViews] = useState(String(initial?.min_views ?? 1000));
  const [maxPayout, setMaxPayout] = useState(String(initial?.max_payout_per_video ?? 75));
  const [paymentMethods, setPaymentMethods] = useState(initial?.payment_methods_allowed || ["cashapp", "paypal", "venmo", "crypto"]);
  const [bonusRewards, setBonusRewards] = useState(initial?.bonus_rewards || []);
  const [streamerImageUrl, setStreamerImageUrl] = useState(initial?.streamer_image_url || "");
  const [streamerImageUploading, setStreamerImageUploading] = useState(false);
  const [streamerVerified, setStreamerVerified] = useState(initial?.streamer_verified !== false);
  const [uploadStreakBonus, setUploadStreakBonus] = useState(String(initial?.upload_streak_bonus ?? 0));
  const [viralClipBonus, setViralClipBonus] = useState(String(initial?.viral_clip_bonus ?? 0));
  const [leaderboardBonus, setLeaderboardBonus] = useState(String(initial?.leaderboard_bonus ?? 0));
  const [budget, setBudget] = useState(String(initial?.budget ?? 0));
  const [autoStop, setAutoStop] = useState(Boolean(initial?.auto_stop_on_budget_end));
  const [neonColor, setNeonColor] = useState(initial?.neon_color || "#c8ff00");
  const [snakeEnabled, setSnakeEnabled] = useState(initial?.snake_border_enabled !== false);
  const [snakeSpeed, setSnakeSpeed] = useState(Number(initial?.snake_border_speed ?? 3.5));
  const [snakeBrightness, setSnakeBrightness] = useState(Number(initial?.snake_border_brightness ?? 1));
  const [musicPlatform, setMusicPlatform] = useState(initial?.music_branding?.platform || "");
  const [insightViewers, setInsightViewers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [viewerSearch, setViewerSearch] = useState("");
  const [viewerBusy, setViewerBusy] = useState(false);
  const [musicTitle, setMusicTitle] = useState(initial?.music_branding?.title || "");
  const [musicUrl, setMusicUrl] = useState(initial?.music_branding?.url || "");
  const [links, setLinks] = useState(initial?.links || []);
  const [categories, setCategories] = useState(initial?.categories || []);
  const [campaignType, setCampaignType] = useState(initial?.campaign_type || "Clipping");
  const [tutorialVideos, setTutorialVideos] = useState(() => {
    const list = initial?.tutorial_videos || [];
    if (list.length) return list;
    return initial?.tutorial_video_url ? [{ id: initial?.tutorial_video_file_id || "legacy", url: initial.tutorial_video_url, name: initial?.tutorial_video_name || "Video Tutorial" }] : [];
  });
  const [tutorialUploading, setTutorialUploading] = useState(false);

  // Load insight viewers and user directory on mount (edit mode only)
  useEffect(() => {
    if (!initial?.id) return;
    api.get(`/admin/campaigns/${initial.id}/insight_viewers`)
      .then((r) => setInsightViewers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setInsightViewers([]));
    api.get("/admin/users", { params: { role: "all" } })
      .then((r) => setAllUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllUsers([]));
  }, [initial?.id]);

  async function addInsightViewer(userId) {
    if (!initial?.id || !userId) return;
    setViewerBusy(true);
    try {
      await api.post(`/admin/campaigns/${initial.id}/insight_viewers`, { user_id: userId });
      const r = await api.get(`/admin/campaigns/${initial.id}/insight_viewers`);
      setInsightViewers(Array.isArray(r.data) ? r.data : []);
      toast({ title: "Viewer added", description: "They can now see this campaign on their Insights page." });
    } catch (e) {
      toast({ title: "Couldn't add viewer", description: apiErrorMessage(e) });
    } finally {
      setViewerBusy(false);
    }
  }

  async function removeInsightViewer(userId) {
    if (!initial?.id || !userId) return;
    setViewerBusy(true);
    try {
      await api.delete(`/admin/campaigns/${initial.id}/insight_viewers/${userId}`);
      setInsightViewers((prev) => prev.filter((v) => v.user_id !== userId));
    } catch (e) {
      toast({ title: "Couldn't remove viewer", description: apiErrorMessage(e) });
    } finally {
      setViewerBusy(false);
    }
  }

  function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setBannerDataUrl(String(reader.result || ""));
    reader.readAsDataURL(f);
  }

  async function onTutorialFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setTutorialUploading(true);
    try {
      const uploaded = await uploadFile(f, "campaign-tutorial");
      setTutorialVideos((prev) => [...prev.slice(0, 2), { id: uploaded.id, url: uploaded.file_url, name: uploaded.original_filename || f.name }]);
      toast({ title: "Tutorial uploaded", description: "Video tutorial is ready to save with the campaign." });
    } catch (err) {
      toast({ title: "Upload failed", description: apiErrorMessage(err) });
    } finally {
      setTutorialUploading(false);
    }
  }

  return (
    <div data-testid="campaign-form" className="space-y-3">
      <div className="grid gap-2">
        <label data-testid="campaign-name-label" className="text-sm text-foreground">
          Campaign name
        </label>
        <Input
          data-testid="campaign-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Winter drops – viral hooks"
          className="border-border/60 bg-background text-foreground"
        />
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-creator-label" className="text-sm text-foreground">
          Creator / brand
        </label>
        <Input
          data-testid="campaign-creator-input"
          value={creatorBrand}
          onChange={(e) => setCreatorBrand(e.target.value)}
          placeholder="e.g., BrandName"
          className="border-border/60 bg-background text-foreground"
        />
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-target-views-label" className="text-sm text-foreground">
          Target views (budget)
        </label>
        <Input
          data-testid="campaign-target-views-input"
          value={targetViews}
          onChange={(e) => setTargetViews(e.target.value)}
          placeholder="e.g., 100000"
          className="border-border/60 bg-background text-foreground"
        />
        <div className="text-xs text-muted-foreground">
          Progress will be based on accepted views (rejected won&apos;t count).
        </div>
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-description-label" className="text-sm text-foreground">
          Description
        </label>
        {me?.role === "admin" ? (
          <div className="rounded-xl border border-border/60 bg-background">
            <RichTextEditor
              value={description}
              onChange={(v) => {
                setDescription(v);
                setDescriptionFormat("html");
              }}
            />
          </div>
        ) : (
          <Textarea
            data-testid="campaign-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief details (supports line breaks)"
            className="min-h-[120px] border-border/60 bg-background text-foreground whitespace-pre-wrap break-words"
          />
        )}
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-platforms-label" className="text-sm text-foreground">
          Platforms allowed
        </label>
        <div data-testid="campaign-platforms" className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => {
            const active = platforms.includes(p);
            return (
              <Button
                key={p}
                data-testid={`campaign-platform-${p}`}
                type="button"
                variant="secondary"
                className={
                  active
                    ? "rounded-full bg-cyan-500/20 text-cyan-700 dark:text-cyan-700 dark:text-cyan-300 hover:bg-cyan-500/25"
                    : "rounded-full bg-muted text-foreground hover:bg-muted/80"
                }
                onClick={() =>
                  setPlatforms((prev) =>
                    prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
                  )
                }
              >
                <span className="inline-flex items-center gap-2">
                  <img
                    alt={`${p} icon`}
                    src={PLATFORM_ICONS[p]}
                    className="h-4 w-4 rounded-sm"
                  />
                  {p}
                </span>
              </Button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-categories-label" className="text-sm text-foreground">Categories</label>
        <div data-testid="campaign-categories" className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => {
            const active = categories.includes(cat);
            return <Button key={cat} data-testid={`campaign-category-${cat}`} type="button" variant="secondary" className={active ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full bg-muted text-foreground hover:bg-muted/80"} onClick={() => setCategories((prev) => prev.includes(cat) ? prev.filter((x) => x !== cat) : [...prev, cat])}>{cat}</Button>;
          })}
        </div>
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-type-label" className="text-sm text-foreground">Campaign Type</label>
        <div data-testid="campaign-type-selector" className="flex flex-wrap gap-2">
          {CAMPAIGN_TYPES.map((type) => <Button key={type} type="button" variant="secondary" className={campaignType === type ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full bg-muted text-foreground hover:bg-muted/80"} onClick={() => setCampaignType(type)}>{type}</Button>)}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="grid gap-2">
          <label data-testid="campaign-payout-label" className="text-sm text-foreground">
            CPM / payout rate
          </label>
          <Input
            data-testid="campaign-payout-input"
            value={payout}
            onChange={(e) => setPayout(e.target.value)}
            placeholder="e.g., 8"
            className="border-border/60 bg-background text-foreground"
          />
        </div>
        <div className="grid gap-2">
          <label data-testid="campaign-threshold-label" className="text-sm text-foreground">
            Payout threshold
          </label>
          <Input
            data-testid="campaign-threshold-input"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder="e.g., 50"
            className="border-border/60 bg-background text-foreground"
          />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="grid gap-2"><label className="text-sm text-foreground">Minimum Views</label><Input data-testid="campaign-min-views-input" value={minViews} onChange={(e)=>setMinViews(e.target.value)} placeholder="1K" className="border-border/60 bg-background text-foreground" /></div>
        <div className="grid gap-2"><label className="text-sm text-foreground">Max Payout Per Video</label><Input data-testid="campaign-max-payout-input" value={maxPayout} onChange={(e)=>setMaxPayout(e.target.value)} placeholder="$75" className="border-border/60 bg-background text-foreground" /></div>
        <div className="grid gap-2"><label className="text-sm text-foreground">Campaign Budget</label><Input data-testid="campaign-budget-input" value={budget} onChange={(e)=>setBudget(e.target.value)} placeholder="$0" className="border-border/60 bg-background text-foreground" /></div>
      </div>

      <div className="grid gap-2"><label className="text-sm text-foreground">Payment Methods</label><div className="flex flex-wrap gap-2">{[["cashapp","Cash App"],["paypal","PayPal"],["venmo","Venmo"],["crypto","Crypto"]].map(([key,label]) => <Button key={key} data-testid={`campaign-payment-${key}`} type="button" variant="secondary" className={paymentMethods.includes(key) ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full bg-muted text-foreground hover:bg-muted/80"} onClick={()=>setPaymentMethods(prev => prev.includes(key) ? prev.filter(x=>x!==key) : [...prev,key])}>{label}</Button>)}</div></div>
      <div className="grid gap-2"><label className="text-sm text-foreground">Bonus Rewards</label><div className="flex flex-wrap gap-2">{[["viral","Viral Clips"],["leaderboard","Top Leaderboard Rank"],["streak","Streak Uploads"]].map(([key,label]) => <Button key={key} data-testid={`campaign-bonus-${key}`} type="button" variant="secondary" className={bonusRewards.includes(key) ? "rounded-full bg-[#c8ff00] text-black hover:bg-[#b8ef00]" : "rounded-full bg-muted text-foreground hover:bg-muted/80"} onClick={()=>setBonusRewards(prev => prev.includes(key) ? prev.filter(x=>x!==key) : [...prev,key])}>{label}</Button>)}</div></div>

      {/* Bonus amounts (expandable card section) */}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="grid gap-2">
          <label className="text-sm text-foreground">Upload Streak Bonus ($)</label>
          <Input data-testid="campaign-upload-streak-bonus-input" value={uploadStreakBonus} onChange={(e)=>setUploadStreakBonus(e.target.value)} placeholder="0" className="border-border/60 bg-background text-foreground" />
        </div>
        <div className="grid gap-2">
          <label className="text-sm text-foreground">Viral Clip Bonus ($)</label>
          <Input data-testid="campaign-viral-clip-bonus-input" value={viralClipBonus} onChange={(e)=>setViralClipBonus(e.target.value)} placeholder="0" className="border-border/60 bg-background text-foreground" />
        </div>
        <div className="grid gap-2">
          <label className="text-sm text-foreground">Leaderboard Bonus ($)</label>
          <Input data-testid="campaign-leaderboard-bonus-input" value={leaderboardBonus} onChange={(e)=>setLeaderboardBonus(e.target.value)} placeholder="0" className="border-border/60 bg-background text-foreground" />
        </div>
      </div>

      {/* Streamer small image + verified */}
      <div className="grid gap-3 rounded-2xl border border-border/60 bg-background/40 p-3 md:grid-cols-[88px_1fr_auto]">
        <div className="flex flex-col items-center gap-2">
          <div className="relative h-16 w-16 overflow-hidden rounded-full border border-border/60 bg-muted">
            {streamerImageUrl ? (
              <img data-testid="campaign-streamer-image-preview" alt="Streamer" src={streamerImageUrl} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">No image</div>
            )}
          </div>
        </div>
        <div className="grid gap-2">
          <label className="text-sm text-foreground">Streamer image (small avatar shown on the card)</label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              data-testid="campaign-streamer-image-url-input"
              value={streamerImageUrl}
              onChange={(e)=>setStreamerImageUrl(e.target.value)}
              placeholder="Paste image URL or upload below"
              className="border-border/60 bg-background text-foreground"
            />
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-border/60 bg-background px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted/50">
              <Upload size={12} /> {streamerImageUploading ? "Uploading…" : "Upload"}
              <input
                data-testid="campaign-streamer-image-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setStreamerImageUploading(true);
                  try {
                    const res = await uploadFile(f, "campaign_streamer");
                    const url = res?.url || res?.file_url || (res?.id ? `/api/files/${res.id}` : null);
                    if (url) setStreamerImageUrl(url);
                  } catch (err) {
                    toast({ title: "Upload failed", description: apiErrorMessage(err) });
                  } finally {
                    setStreamerImageUploading(false);
                    if (e.target) e.target.value = "";
                  }
                }}
              />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">Square image works best — appears as a small avatar next to the streamer name on the campaign card.</p>
        </div>
        <label className="flex items-center gap-2 self-start rounded-xl border border-border/60 bg-background px-3 py-2 text-sm">
          <Checkbox
            data-testid="campaign-streamer-verified-checkbox"
            checked={streamerVerified}
            onCheckedChange={(v)=>setStreamerVerified(Boolean(v))}
          />
          <span className="flex items-center gap-1 text-foreground"><BadgeCheck size={14} className="text-sky-400" /> Verified</span>
        </label>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="grid gap-2">
          <label data-testid="campaign-status-label" className="text-sm text-foreground">
            Status
          </label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              data-testid="campaign-status-select"
              className="border-border/60 bg-background text-foreground"
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem data-testid={`campaign-status-option-${s}`} key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <label data-testid="campaign-card-color-label" className="text-sm text-foreground">
            Card color
          </label>
          <div className="flex items-center gap-2">
            <input
              data-testid="campaign-card-color-input"
              type="color"
              value={cardColor || "#8B5CF6"}
              onChange={(e) => setCardColor(e.target.value)}
              className="h-9 w-12 cursor-pointer rounded-lg border border-border/60 bg-background p-0.5"
            />
            <Input
              data-testid="campaign-card-color-text"
              value={cardColor}
              onChange={(e) => setCardColor(e.target.value)}
              placeholder="#8B5CF6 or empty for default"
              className="border-border/60 bg-background text-foreground"
            />
            {cardColor && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 text-xs text-muted-foreground"
                onClick={() => setCardColor("")}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
        <div className="grid gap-2">
          <label data-testid="campaign-progress-color-label" className="text-sm text-foreground">Progress Bar Color</label>
          <input data-testid="campaign-progress-color-input" type="color" value={progressColor || "#c8ff00"} onChange={(e) => setProgressColor(e.target.value)} className="h-9 w-16 cursor-pointer rounded-lg border border-border/60 bg-background p-0.5" />
        </div>
      </div>
      <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-3 py-2 text-sm text-muted-foreground"><input data-testid="campaign-auto-stop-checkbox" type="checkbox" checked={autoStop} onChange={(e)=>setAutoStop(e.target.checked)} /> Auto stop when budget ends</label>

      <div className="grid gap-2">
        <label data-testid="campaign-banner-url-label" className="text-sm text-foreground">
          Campaign image (URL)
        </label>
        <Input
          data-testid="campaign-banner-url-input"
          value={bannerUrl}
          onChange={(e) => setBannerUrl(e.target.value)}
          placeholder="https://..."
          className="border-border/60 bg-background text-foreground"
        />
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-banner-upload-label" className="text-sm text-foreground">
          Or upload image
        </label>
        <Input
          data-testid="campaign-banner-upload-input"
          type="file"
          accept="image/*"
          onChange={onFile}
          className="border-border/60 bg-background text-foreground"
        />
        {bannerDataUrl ? (
          <div data-testid="campaign-banner-preview" className="mt-2 overflow-hidden rounded-2xl border border-border/60">
            <img src={bannerDataUrl} alt="Campaign" className="h-36 w-full object-cover" />
          </div>
        ) : null}
      </div>

      <div className="grid gap-2">
        <label data-testid="campaign-tutorial-upload-label" className="text-sm text-foreground">Video tutorials (up to 3)</label>
        <label data-testid="campaign-tutorial-upload" className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-border/60 bg-background px-4 py-3 text-sm text-muted-foreground hover:border-[#c8ff00]/40">
          <Upload size={14} /> {tutorialUploading ? "Uploading..." : tutorialVideos.length >= 3 ? "Maximum 3 tutorials uploaded" : "Upload tutorial video"}
          <Input type="file" accept="video/*" onChange={onTutorialFile} disabled={tutorialVideos.length >= 3} className="hidden" />
        </label>
        <div className="grid gap-3 md:grid-cols-3">
          {tutorialVideos.map((vid, idx) => <div key={vid.id || idx} className="relative overflow-hidden rounded-2xl border border-border/60 bg-black"><button type="button" data-testid={`campaign-tutorial-delete-${idx}`} onClick={() => setTutorialVideos((prev)=>prev.filter((_,i)=>i!==idx))} className="absolute right-2 top-2 z-10 rounded-full bg-black/70 p-1.5 text-rose-300 hover:bg-rose-500/20"><Trash2 size={14}/></button><video data-testid={`campaign-tutorial-preview-${idx}`} controls src={vid.url} className="aspect-video w-full bg-black" /><div className="truncate px-3 py-2 text-xs text-muted-foreground">{vid.name}</div></div>)}
        </div>
      </div>

      {/* Links Section */}
      <div className="grid gap-2">
        <label className="text-sm text-foreground">Links</label>
        {links.map((link, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input
              value={link.title}
              onChange={(e) => {
                const copy = [...links];
                copy[idx] = { ...copy[idx], title: e.target.value };
                setLinks(copy);
              }}
              placeholder="Link title"
              className="flex-1 border-border/60 bg-background text-foreground"
            />
            <Input
              value={link.url}
              onChange={(e) => {
                const copy = [...links];
                copy[idx] = { ...copy[idx], url: e.target.value };
                setLinks(copy);
              }}
              placeholder="https://..."
              className="flex-1 border-border/60 bg-background text-foreground"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 h-9 text-rose-500 hover:bg-rose-500/10"
              onClick={() => setLinks(links.filter((_, j) => j !== idx))}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="w-fit rounded-full border border-dashed border-border/60 text-xs"
          onClick={() => setLinks([...links, { title: "", url: "" }])}
        >
          + Add Link
        </Button>
      </div>

      <div className="grid gap-3 rounded-2xl border border-border/60 bg-card/50 p-4">
        <div>
          <div className="text-sm font-semibold text-foreground">Music & Branding</div>
          <div className="text-xs text-muted-foreground">Optional sound or branding link clippers should use (TikTok sound, IG audio, YouTube track).</div>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          <Select value={musicPlatform} onValueChange={setMusicPlatform}>
            <SelectTrigger data-testid="campaign-music-platform" className="border-border/60 bg-background"><SelectValue placeholder="Platform" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tiktok"><span className="inline-flex items-center gap-2"><PlatformIcon platform="tiktok" size={14} /> TikTok</span></SelectItem>
              <SelectItem value="instagram"><span className="inline-flex items-center gap-2"><PlatformIcon platform="instagram" size={14} /> Instagram</span></SelectItem>
              <SelectItem value="youtube"><span className="inline-flex items-center gap-2"><PlatformIcon platform="youtube" size={14} /> YouTube</span></SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="campaign-music-title" value={musicTitle} onChange={(e) => setMusicTitle(e.target.value)} className="border-border/60 bg-background" placeholder="Title (e.g. Original sound)" />
          <Input data-testid="campaign-music-url" value={musicUrl} onChange={(e) => setMusicUrl(e.target.value)} className="border-border/60 bg-background" placeholder="https://..." />
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-[#c8ff00]/20 bg-[#c8ff00]/[0.03] p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-foreground">Animated Snake Border</div>
            <div className="text-xs text-muted-foreground">A traveling neon edge animation around the campaign card.</div>
          </div>
          <Switch data-testid="campaign-snake-enabled" checked={snakeEnabled} onCheckedChange={setSnakeEnabled} />
        </div>

        <div className={`grid gap-3 sm:grid-cols-3 transition-opacity ${snakeEnabled ? "opacity-100" : "pointer-events-none opacity-50"}`}>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Color</label>
            <div className="flex items-center gap-2">
              <input
                data-testid="campaign-snake-color-picker"
                type="color"
                value={neonColor}
                onChange={(e) => setNeonColor(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded-md border border-border/60 bg-background"
                aria-label="Snake border color"
              />
              <Input
                data-testid="campaign-snake-color-hex"
                value={neonColor}
                onChange={(e) => setNeonColor(e.target.value)}
                className="h-9 flex-1 border-border/60 bg-background font-mono text-xs"
                placeholder="#c8ff00"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center justify-between text-xs font-medium text-foreground">
              <span>Speed</span>
              <span data-testid="campaign-snake-speed-value" className="font-mono text-[10px] text-muted-foreground">{snakeSpeed.toFixed(1)}s / loop</span>
            </label>
            <input
              data-testid="campaign-snake-speed-slider"
              type="range"
              min="1"
              max="10"
              step="0.5"
              value={snakeSpeed}
              onChange={(e) => setSnakeSpeed(Number(e.target.value))}
              className="h-9 w-full cursor-pointer accent-[#c8ff00]"
              aria-label="Snake border speed"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground"><span>Faster</span><span>Slower</span></div>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center justify-between text-xs font-medium text-foreground">
              <span>Brightness</span>
              <span data-testid="campaign-snake-brightness-value" className="font-mono text-[10px] text-muted-foreground">{Math.round(snakeBrightness * 100)}%</span>
            </label>
            <input
              data-testid="campaign-snake-brightness-slider"
              type="range"
              min="0.2"
              max="1.5"
              step="0.05"
              value={snakeBrightness}
              onChange={(e) => setSnakeBrightness(Number(e.target.value))}
              className="h-9 w-full cursor-pointer accent-[#c8ff00]"
              aria-label="Snake border brightness"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground"><span>Dim</span><span>Bright</span></div>
          </div>
        </div>

        {/* Live preview */}
        <div className="mt-1 flex items-center justify-center">
          {snakeEnabled ? (
            <div
              data-testid="campaign-snake-preview"
              className="snake-border"
              style={{
                "--neon": neonColor,
                "--snake-speed": `${snakeSpeed}s`,
                "--snake-brightness": String(snakeBrightness),
              }}
            >
              <div className="rounded-2xl border border-border/60 bg-background px-6 py-4 text-xs text-muted-foreground">
                Live preview — your card will glow like this.
              </div>
            </div>
          ) : (
            <div data-testid="campaign-snake-preview-off" className="rounded-2xl border border-dashed border-border/60 bg-background px-6 py-4 text-xs text-muted-foreground">
              Snake border is OFF — card will use the standard border.
            </div>
          )}
        </div>
      </div>

      {initial?.id && (
        <div data-testid="campaign-insight-viewers-section" className="grid gap-3 rounded-2xl border border-border/60 bg-card/50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-foreground">Insights Viewers</div>
              <div className="text-xs text-muted-foreground">Brands / streamers who can view this campaign's analytics on their Insights page.</div>
            </div>
            <span className="inline-flex items-center rounded-full bg-[#c8ff00]/15 px-2.5 py-1 text-[11px] font-bold text-[#c8ff00]">
              {insightViewers.length} assigned
            </span>
          </div>

          {insightViewers.length > 0 && (
            <div className="space-y-1.5">
              {insightViewers.map((v) => (
                <div key={v.user_id} data-testid={`insight-viewer-row-${v.user_id}`} className="flex items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 text-xs">
                  <img alt="" src={v.user?.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${v.user?.discord_username || v.user?.email || "U"}`} className="h-6 w-6 rounded-full" />
                  <div className="min-w-0 flex-1 truncate">
                    <span className="font-semibold">{v.user?.discord_username || v.user?.email || v.user_id.slice(0, 8)}</span>
                    <span className="ml-2 text-[10px] text-muted-foreground capitalize">{(v.user?.roles || [v.user?.role || "user"]).join(", ")}</span>
                  </div>
                  <button
                    type="button"
                    data-testid={`insight-viewer-remove-${v.user_id}`}
                    onClick={() => removeInsightViewer(v.user_id)}
                    disabled={viewerBusy}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-border/60 text-muted-foreground hover:border-rose-500/50 hover:text-rose-400 disabled:opacity-40"
                    aria-label="Remove viewer"
                  >
                    <XIcon size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
            <Input
              data-testid="insight-viewer-search"
              value={viewerSearch}
              onChange={(e) => setViewerSearch(e.target.value)}
              placeholder="Search users to add as viewer..."
              className="border-border/60 bg-background"
            />
            <div className="text-[10px] text-muted-foreground sm:text-right">Start typing to filter</div>
          </div>
          {viewerSearch.trim() && (
            <div className="max-h-[180px] space-y-1 overflow-y-auto">
              {allUsers
                .filter((u) => !insightViewers.some((v) => v.user_id === u.id))
                .filter((u) => {
                  const q = viewerSearch.toLowerCase();
                  return (u.discord_username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                })
                .slice(0, 10)
                .map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    data-testid={`insight-viewer-add-${u.id}`}
                    onClick={() => addInsightViewer(u.id)}
                    disabled={viewerBusy}
                    className="flex w-full items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 text-left text-xs transition-colors hover:border-[#c8ff00]/40 hover:bg-[#c8ff00]/[0.04] disabled:opacity-40"
                  >
                    <img alt="" src={u.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${u.discord_username || u.email}`} className="h-6 w-6 rounded-full" />
                    <span className="min-w-0 flex-1 truncate font-semibold">{u.discord_username || u.email}</span>
                    <Plus size={12} className="text-[#c8ff00]" />
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      <Button
        data-testid="campaign-save-button"
        className="w-full rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97]"
        disabled={saving || !name || !creatorBrand}
        onClick={() =>
          onSave({
            name,
            creator_brand: creatorBrand,
            description,
            description_format: descriptionFormat,
            platforms_allowed: platforms,
            payout_cpm: Number(payout || 0),
            payout_threshold: Number(threshold || 0),
            target_views: parseInt(targetViews || "0", 10) || 0,
            status,
            banner_url: bannerUrl || null,
            banner_image_data_url: bannerDataUrl || null,
            card_color: cardColor || null,
            progress_color: progressColor || "#c8ff00",
            min_views: parseInt(minViews || "1000", 10) || 1000,
            max_payout_per_video: Number(maxPayout || 75),
            payment_methods_allowed: paymentMethods,
            bonus_rewards: bonusRewards,
            streamer_image_url: streamerImageUrl || null,
            streamer_verified: streamerVerified,
            upload_streak_bonus: Number(uploadStreakBonus || 0),
            viral_clip_bonus: Number(viralClipBonus || 0),
            leaderboard_bonus: Number(leaderboardBonus || 0),
            budget: Number(budget || 0),
            auto_stop_on_budget_end: autoStop,
            neon_color: neonColor || "#c8ff00",
            snake_border_enabled: snakeEnabled,
            snake_border_speed: Number(snakeSpeed || 3.5),
            snake_border_brightness: Number(snakeBrightness || 1),
            music_branding: (musicTitle.trim() || musicUrl.trim() || musicPlatform)
              ? { platform: musicPlatform || "other", title: musicTitle.trim(), url: musicUrl.trim() }
              : null,
            campaign_type: campaignType,
            links: links.filter((l) => l.title || l.url),
            categories,
            tutorial_video_file_id: tutorialVideos[0]?.id || null,
            tutorial_video_url: tutorialVideos[0]?.url || null,
            tutorial_video_name: tutorialVideos[0]?.name || null,
            tutorial_videos: tutorialVideos,
          })
        }
      >
        {saving ? "Saving…" : "Save campaign"}
      </Button>
    </div>
  );
}

export function cardColorStyle(color) {
  if (!color) return {};
  return { backgroundColor: `${color}18`, borderColor: `${color}30` };
}

function FavoriteButton({ campaignId, isFav, onToggle }) {
  return (
    <button
      data-testid={`campaign-fav-${campaignId}`}
      className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/30 backdrop-blur-sm transition-all hover:scale-110"
      onClick={(e) => { e.stopPropagation(); onToggle(campaignId); }}
    >
      <Star
        size={16}
        className={isFav
          ? "fill-[#c8ff00] text-[#c8ff00] drop-shadow-[0_0_6px_rgba(200,255,0,0.6)]"
          : "text-white/70"
        }
      />
    </button>
  );
}

export function CampaignCardContent({ c, canManage, isAdmin, joinStatus, isFav, onToggleFav, onViewDetails, onJoin, onEdit, onRemove, onOpenSubmit }) {
  const prog = c._progress || {};
  const pct = Number(prog.percent || 0);
  const accepted = Number(prog.accepted_views || 0);
  const target = Number(prog.target_views || 0);
  const totalViews = Number(prog.total_views ?? accepted ?? 0);

  const progressColor = c.progress_color || c.card_color || "#c8ff00";
  const neon = c.neon_color || "#c8ff00";
  const snakeEnabled = c.snake_border_enabled !== false;
  const snakeSpeed = Number(c.snake_border_speed ?? 3.5);
  const snakeBrightness = Number(c.snake_border_brightness ?? 1);
  const snakeStyle = {
    "--neon": neon,
    "--snake-speed": `${snakeSpeed}s`,
    "--snake-brightness": String(snakeBrightness),
  };

  // Status dot color (active = green, paused = yellow, ended = red)
  const statusDotColor = c.status === "active" || c.status === "available"
    ? "#22c55e"
    : c.status === "paused" || c.status === "pending"
    ? "#eab308"
    : "#ef4444";
  const campaignTypeLabel = String(c.campaign_type || "CLIPPING").toUpperCase();
  const budget = Number(c.budget || 0);
  const cpm = Number(c.payout_cpm || 0);
  const paidOut = Number(c.budget_spent || 0);
  const platformsAllowed = (c.platforms_allowed || []).length
    ? c.platforms_allowed
    : ["TikTok", "YouTube", "Instagram"];

  // Long-press (mobile) / right-click (PC) → copy campaign link
  const longPressTimer = React.useRef(null);
  const copyShareLink = React.useCallback(async (e) => {
    try {
      e?.preventDefault?.();
      const link = `${window.location.origin}/campaigns/${c.id}`;
      await navigator.clipboard.writeText(link);
      toast({ title: "Link copied", description: link });
    } catch {
      toast({ title: "Couldn't copy", description: "Your browser blocked clipboard access." });
    }
  }, [c.id]);
  const onTouchStart = (e) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => copyShareLink(e), 550);
  };
  const cancelLongPress = () => { if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; } };

  // Expandable bottom panel
  const [expanded, setExpanded] = useState(false);

  // Streamer image (small avatar) — fall back to creator_avatar_url, then dicebear
  const streamerImg = c.streamer_image_url
    || c.creator_avatar_url
    || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(c.creator_brand || c.name || "C")}&backgroundColor=c8ff00&textColor=000000`;
  const streamerVerified = c.streamer_verified !== false;
  const bgImg = c.banner_image_data_url || c.banner_url;

  // Numeric formatting for the "paid out" row (e.g. $78.5 of 1.7K paid out)
  const fmtMoneyShort = (n) => {
    const v = Number(n || 0);
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;
    if (v >= 100) return `$${v.toFixed(0)}`;
    return `$${v.toFixed(1).replace(/\.0$/, "")}`;
  };
  const budgetShort = (n) => {
    const v = Number(n || 0);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;
    return v.toFixed(0);
  };
  const paidOutPct = budget > 0 ? Math.min(100, Math.round((paidOut / budget) * 100)) : 0;

  const bonusItems = [
    { key: "leaderboard", icon: Trophy, label: "Leaderboard Bonus", value: c.leaderboard_bonus ? `+$${Number(c.leaderboard_bonus).toFixed(0)}` : null, color: "text-amber-300" },
    { key: "viral", icon: Zap, label: "Viral Clip Bonus", value: c.viral_clip_bonus ? `+$${Number(c.viral_clip_bonus).toFixed(0)}` : null, color: "text-[#c8ff00]" },
    { key: "streak", icon: Flame, label: "Upload Streak", value: c.upload_streak_bonus ? `+$${Number(c.upload_streak_bonus).toFixed(0)}/day` : null, color: "text-orange-300" },
    { key: "min_views", icon: Eye, label: "Min Views", value: fmtNumber(c.min_views || 1000), color: "text-sky-300" },
    { key: "max_payout", icon: DollarSign, label: "Max Payout / Video", value: `$${Number(c.max_payout_per_video || 75).toFixed(0)}`, color: "text-emerald-300" },
  ].filter((b) => b.value);

  // Budget-used percentage (drives lime overlay + progress bar)
  const budgetUsedPct = budget > 0 ? Math.min(100, Math.round((paidOut / budget) * 100)) : 0;
  const clipperCount = Number(c.clipper_count ?? c.joined_count ?? prog.joined_count ?? prog.clipper_count ?? 0);
  const budgetRemaining = Math.max(0, budget - paidOut);

  const cardJsx = (
    <Card
      data-testid={`campaign-card-${c.id}`}
      className="cc-card group block w-full cursor-pointer border-0 bg-transparent p-0 shadow-none transition-transform duration-300 hover:-translate-y-1"
      onContextMenu={copyShareLink}
      onTouchStart={onTouchStart}
      onTouchEnd={cancelLongPress}
      onTouchMove={cancelLongPress}
      onTouchCancel={cancelLongPress}
      onClick={() => onViewDetails?.(c)}
    >
      <div className="cc-card-inner">
        {/* Banner with sharp image + frosted pills */}
        <div className="cc-banner">
          {bgImg ? (
            <img
              data-testid={`campaign-image-${c.id}`}
              src={bgImg}
              alt={c.name}
              className="cc-banner-bg"
            />
          ) : (
            <div className="cc-banner-bg cc-banner-placeholder" />
          )}

          {/* Top-left: Clipping type pill (frosted) */}
          <div data-testid={`campaign-type-pill-${c.id}`} className="cc-type-chip">
            <Film size={11} strokeWidth={2.4} />
            <span>{campaignTypeLabel.charAt(0) + campaignTypeLabel.slice(1).toLowerCase()}</span>
          </div>

          {/* Top-right: Join + button only (star removed) */}
          <div className="cc-top-right">
            <button
              type="button"
              aria-label="Add to my campaigns"
              data-testid={`campaign-join-plus-${c.id}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!joinStatus && ["active", "available"].includes(c.status)) onJoin?.(c.id);
                else if (onOpenSubmit) onOpenSubmit(c);
                else onViewDetails?.(c);
              }}
              className="cc-top-btn cc-top-btn-plus"
            >
              <Plus size={16} strokeWidth={2.6} />
            </button>
          </div>
        </div>

        {/* Info row: small avatar + streamer name + verified seal + platform icons */}
        <div className="cc-info-row">
          <img
            alt=""
            src={streamerImg}
            className="cc-info-avatar"
            data-testid={`campaign-streamer-avatar-${c.id}`}
            onError={(e) => {
              e.currentTarget.src = `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(c.creator_brand || c.name || "C")}&backgroundColor=27272a&textColor=ffffff`;
            }}
          />
          <span
            data-testid={`campaign-streamer-name-${c.id}`}
            className="cc-info-streamer"
          >
            {c.creator_brand || c.name}
          </span>
          {streamerVerified && (
            <span data-testid={`campaign-verified-${c.id}`} className="cc-info-verified" aria-label="Verified">
              {c.verified_badge_url ? (
                <img src={c.verified_badge_url} alt="Verified" className="h-full w-full object-cover" />
              ) : (
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <defs>
                    <linearGradient id={`vgrad-${c.id}`} x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#e8ff7a" />
                      <stop offset="55%" stopColor="#c8ff00" />
                      <stop offset="100%" stopColor="#9bd400" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M12 1.2l1.86 1.6 2.43-.34 1.13 2.18 2.18 1.13-.34 2.43L20.86 10 19.26 12l1.6 1.86-.34 2.43-2.18 1.13-1.13 2.18-2.43-.34L12 20.86 10.14 19.26l-2.43.34-1.13-2.18-2.18-1.13.34-2.43L3.14 12l1.6-1.86-.34-2.43 2.18-1.13 1.13-2.18 2.43.34L12 1.2z"
                    fill={`url(#vgrad-${c.id})`}
                    stroke="rgba(0,0,0,0.06)"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M7.5 12.4l3 3 6.2-6.6"
                    stroke="#ffffff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              )}
            </span>
          )}
          <div className="cc-info-platforms">
            {platformsAllowed.slice(0, 3).map((p) => (
              PLATFORM_ICONS[p] ? (
                <span key={p} className="cc-info-platform-chip" data-testid={`campaign-platform-icon-${c.id}-${p}`}>
                  <img alt={p} src={PLATFORM_ICONS[p]} className="h-4 w-4 object-contain" />
                </span>
              ) : null
            ))}
          </div>
        </div>

        {/* Campaign title (its own row) */}
        <h3
          data-testid={`campaign-name-${c.id}`}
          className="cc-info-title"
        >
          {c.name}
        </h3>

        {/* Bottom row: "X% Budget Used" left + stat pills right */}
        <div className="cc-bottom-row">
          <span data-testid={`campaign-paid-out-${c.id}`} className="cc-banner-budget">
            <span className="cc-banner-budget-pct tabular-nums">{budgetUsedPct}%</span>
            <span className="cc-banner-budget-label">Budget Used</span>
          </span>
        {/* Stats row: connected pill-shape badges (User Count, Budget, CPM) */}
        <div className="cc-stats-row">
          <div className="cc-stats-chain" data-testid={`campaign-stats-chain-${c.id}`}>
            <span data-testid={`campaign-clipper-count-${c.id}`} className="cc-stats-pill">
              <Users size={12} strokeWidth={2.4} className="cc-stats-pill-icon" />
              <span className="tabular-nums">{clipperCount}</span>
            </span>
            <span data-testid={`campaign-budget-pill-${c.id}`} className="cc-stats-pill">
              <Wallet size={12} strokeWidth={2.4} className="cc-stats-pill-icon" />
              <span className="tabular-nums">{budgetShort(budgetRemaining)}</span>
            </span>
            <span data-testid={`campaign-cpm-pill-${c.id}`} className="cc-stats-pill">
              <DollarSign size={12} strokeWidth={2.6} className="cc-stats-pill-icon" />
              <span className="tabular-nums">${cpm.toFixed(2)}</span>
            </span>
          </div>
          <span
            data-testid={`campaign-status-dot-${c.id}`}
            className="cc-stats-dot"
            style={{ background: statusDotColor, boxShadow: `0 0 0 4px color-mix(in srgb, ${statusDotColor} 25%, transparent)` }}
            title={String(c.status || "active")}
          />
        </div>
        </div>

        {/* Lime progress bar — flush at the very bottom of the card */}
        <div data-testid={`campaign-progress-${c.id}`} className="cc-banner-progress">
          <div className="cc-banner-progress-fill" style={{ width: `${budgetUsedPct}%` }} />
        </div>

        {/* Manager actions overlay (admin/mod) */}
        {canManage && (
          <div className="absolute right-3 top-14 z-30 flex flex-col gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <Button
              data-testid={`campaign-edit-${c.id}`}
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full bg-black/70 text-white backdrop-blur hover:bg-black/90"
              onClick={(e) => { e.stopPropagation(); onEdit(c); }}
              title="Edit"
            >
              <Pencil size={12} />
            </Button>
            {isAdmin && (
              <Button
                data-testid={`campaign-delete-${c.id}`}
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full bg-black/70 text-white backdrop-blur hover:bg-rose-500/80"
                onClick={(e) => { e.stopPropagation(); onRemove(c.id); }}
                title="Remove"
              >
                <Trash2 size={12} />
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );

  return snakeEnabled ? (
    <div className="snake-border cc-card-shell" style={snakeStyle} data-testid={`campaign-snake-${c.id}`}>
      {cardJsx}
    </div>
  ) : cardJsx;
}

function FeaturedCampaignsCarousel({ items, onView, onJoin, joinedIds }) {
  const featured = useMemo(() => (items || []).slice(0, 6), [items]);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const prevIndexRef = React.useRef(0);

  React.useEffect(() => {
    setDirection(index >= prevIndexRef.current ? 1 : -1);
    prevIndexRef.current = index;
  }, [index]);

  useEffect(() => {
    if (featured.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % featured.length), 7000);
    return () => clearInterval(t);
  }, [featured.length]);

  if (!featured.length) return null;

  const c = featured[index];
  const banner = c?.banner_image_data_url || c?.banner_url;
  const avatar = c?.streamer_image_data_url || c?.streamer_image_url || c?.creator_image_data_url || c?.creator_image_url || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(c?.creator_brand || c?.name || "C")}&backgroundColor=c8ff00&textColor=000000`;
  const verified = !!(c?.streamer_verified ?? c?.creator_verified);
  const cpm = Number(c?.payout_cpm || 0);
  const budget = Number(c?.budget_total || 0);
  const isJoined = joinedIds?.has?.(c.id);
  const ctype = String(c?.campaign_type || "Clipping");
  const niceType = ctype.charAt(0).toUpperCase() + ctype.slice(1).toLowerCase();

  const slideDirClass = direction > 0 ? "featured-slide-from-right" : "featured-slide-from-left";

  return (
    <div
      data-testid="featured-campaigns-carousel"
      className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#0a0a0a] shadow-[0_24px_70px_-24px_rgba(0,0,0,0.75)]"
    >
      {/* Featured label — top-left corner (rectangular pill per new design) */}
      <div data-testid="featured-label" className="absolute left-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-lg border border-[#c8ff00]/40 bg-[#c8ff00]/15 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-widest text-[#c8ff00] backdrop-blur sm:left-6 sm:top-6 lg:left-10">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c8ff00] shadow-[0_0_6px_rgba(200,255,0,0.7)] animate-pulse" />
        Featured
      </div>

      {/* Background banner — shorter on desktop. Image fully visible. */}
      <div className="relative h-[320px] w-full sm:h-[420px] lg:h-[500px]">
        {banner ? (
          <>
            {/* Blurred backdrop fills the entire area */}
            <img
              key={`bgblur-${c.id}-${index}`}
              aria-hidden="true"
              src={banner}
              alt=""
              className={`absolute inset-0 h-full w-full object-cover ${slideDirClass}`}
              style={{ filter: "blur(28px) saturate(140%) brightness(0.6)", transform: "scale(1.18)" }}
            />
            {/* Sharp contained foreground — full image always visible, no crop */}
            <img
              key={`bg-${c.id}-${index}`}
              data-testid={`featured-banner-${c.id}`}
              src={banner}
              alt=""
              className={`absolute inset-0 mx-auto h-full w-full object-contain ${slideDirClass}`}
            />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#0a0a0a] to-[#000]" />
        )}
        {/* Dark fade for legibility */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_50%,transparent_0%,rgba(0,0,0,0.25)_100%)]" />

        {/* Bottom content — smaller on mobile */}
        <div
          key={`content-${c.id}-${index}`}
          className="featured-fade-up absolute inset-x-0 bottom-0 flex flex-col gap-1.5 px-4 pb-6 pt-4 sm:gap-2.5 sm:px-8 sm:pb-10 lg:px-12"
        >
          {/* Brand row */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <img
              src={avatar}
              alt=""
              data-testid="featured-creator-avatar"
              className="h-6 w-6 rounded-full border border-white/15 object-cover shadow-[0_2px_8px_-2px_rgba(0,0,0,0.55)] sm:h-8 sm:w-8"
            />
            <span data-testid="featured-creator-brand" className="text-[11.5px] font-semibold !text-white/90 sm:text-[13px]">
              {c.creator_brand || "Creator"}
            </span>
            {verified && (
              <span className="inline-flex h-[14px] w-[14px] items-center justify-center sm:h-5 sm:w-5">
                {c.verified_badge_url ? (
                  <img src={c.verified_badge_url} alt="Verified" className="h-full w-full rounded-full object-cover" />
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <defs>
                      <linearGradient id={`fgv-${c.id}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#e8ff7a" />
                        <stop offset="55%" stopColor="#c8ff00" />
                        <stop offset="100%" stopColor="#9bd400" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M12 1.2l1.86 1.6 2.43-.34 1.13 2.18 2.18 1.13-.34 2.43L20.86 10 19.26 12l1.6 1.86-.34 2.43-2.18 1.13-1.13 2.18-2.43-.34L12 20.86 10.14 19.26l-2.43.34-1.13-2.18-2.18-1.13.34-2.43L3.14 12l1.6-1.86-.34-2.43 2.18-1.13 1.13-2.18 2.43.34L12 1.2z"
                      fill={`url(#fgv-${c.id})`}
                      stroke="rgba(0,0,0,0.08)"
                      strokeWidth="0.6"
                    />
                    <path d="M7.5 12.4l3 3 6.2-6.6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                )}
              </span>
            )}
          </div>

          <h2
            data-testid="featured-title"
            className="max-w-[640px] text-[16px] font-black leading-[1.1] !text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)] sm:text-[22px] lg:text-[28px]"
            style={{ fontFamily: '"Archivo Black", system-ui, sans-serif' }}
          >
            {c.name}
          </h2>

          <div data-testid="featured-subtitle" className="text-[11px] font-medium !text-white/75 sm:text-[13.5px]">
            {niceType}
            <span className="mx-1.5">·</span>
            <span className="font-bold !text-white">${cpm.toFixed(cpm < 1 ? 2 : 0)}/1K views</span>
            {budget > 0 ? (
              <>
                <span className="mx-1.5">·</span>
                <span className="font-bold !text-white">${budget.toLocaleString()}</span>
              </>
            ) : null}
          </div>

          <div className="mt-1 flex items-end justify-between gap-3 sm:mt-2">
            <button
              type="button"
              data-testid="featured-join-cta"
              onClick={() => {
                if (!isJoined && ["active", "available"].includes(c.status)) onJoin?.(c.id);
                else onView?.(c);
              }}
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-white px-4 py-2 text-[12px] font-bold text-black shadow-[0_10px_30px_-12px_rgba(255,255,255,0.45)] transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] sm:px-6 sm:py-3 sm:text-[13.5px]"
            >
              <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#c8ff00]/30 to-transparent" />
              <span className="relative z-10">
                {isJoined ? "Open Campaign" : "Join Campaign"}
              </span>
            </button>

            {featured.length > 1 ? (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  aria-label="Previous"
                  data-testid="featured-prev"
                  onClick={() => setIndex((i) => (i - 1 + featured.length) % featured.length)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur transition-all hover:bg-white/20 sm:h-9 sm:w-9"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  aria-label="Next"
                  data-testid="featured-next"
                  onClick={() => setIndex((i) => (i + 1) % featured.length)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur transition-all hover:bg-white/20 sm:h-9 sm:w-9"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Pagination dots */}
        {featured.length > 1 ? (
          <div data-testid="featured-dots" className="absolute inset-x-0 bottom-1.5 flex justify-center gap-1.5">
            {featured.map((_, i) => (
              <button
                key={i}
                type="button"
                data-testid={`featured-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}


export function CampaignsPage() {
  const { me, hasAccess } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [detailsCampaign, setDetailsCampaign] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsInitialTab, setDetailsInitialTab] = useState("overview");
  const [detailsSubmissions, setDetailsSubmissions] = useState([]);

  const joinStatusById = useMemo(() => {
    const m = new Map();
    (joined || []).forEach((x) => m.set(x.id, true));
    return m;
  }, [joined]);

  const joinedIdSet = useMemo(() => new Set((joined || []).map((x) => x.id)), [joined]);

  const filteredItems = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((c) => {
      const hay = [
        c.name,
        c.creator_brand,
        c.campaign_type,
        c.description,
        ...(c.platforms_allowed || []),
        ...(c.platforms || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const featuredItems = useMemo(() => {
    const all = items || [];
    const active = all.filter((c) => ["active", "available"].includes(c.status));
    const pool = active.length ? active : all;
    return [...pool].sort((a, b) => Number(b.budget_total || 0) - Number(a.budget_total || 0));
  }, [items]);

  const queryParams = useMemo(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (platform !== "all") params.platform = platform;
    return params;
  }, [status, platform]);

  const isAdmin = me?.role === "admin" || (me?.roles || []).includes("admin");
  const isMod = me?.role === "mod" || (me?.roles || []).includes("mod");
  const canManage = hasAccess(["campaign_management"], ["create_campaigns", "manage_campaigns"]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));


  async function load() {
    setError(null);
    setLoading(true);
    try {
      const res = await api.get("/campaigns", { params: queryParams });
      setItems(res.data || []);
      loadProgressForCampaigns(res.data || []);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadJoined() {
    try {
      const res = await api.get("/campaigns/joined");
      setJoined(res.data || []);
    } catch {
      setJoined([]);
    }
  }

  async function loadFavorites() {
    try {
      const res = await api.get("/campaigns/favorites");
      setFavorites(new Set(res.data || []));
    } catch {
      setFavorites(new Set());
    }
  }

  async function toggleFavorite(campaignId) {
    try {
      const res = await api.post(`/campaigns/${campaignId}/favorite`);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (res.data?.favorited) next.add(campaignId);
        else next.delete(campaignId);
        return next;
      });
    } catch (e) {
      toast({ title: "Error", description: apiErrorMessage(e) });
    }
  }

  useEffect(() => {
    load();
    loadJoined();
    loadFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, platform]);

  async function join(id) {
    setError(null);
    try {
      const res = await api.post(`/campaigns/${id}/join`);
      await load();
      await loadJoined();
      const campaignName = res.data?.campaign_name || items.find((x) => x.id === id)?.name || "";
      toast({
        title: "Successfully joined",
        description: `Successfully joined "${campaignName}" campaign`,
      });
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function loadProgressForCampaigns(campaignList) {
    // best-effort: only fetch progress for campaigns that have a target view budget
    const withBudget = (campaignList || []).filter((c) => Number(c.target_views || 0) > 0);
    const results = {};

    await Promise.all(
      withBudget.map(async (c) => {
        try {
          const res = await api.get(`/campaigns/${c.id}/progress`);
          results[c.id] = res.data;
        } catch {
          // ignore
        }
      })
    );

    setItems((prev) => prev.map((c) => (results[c.id] ? { ...c, _progress: results[c.id] } : c)));
  }

  function openDetails(c, tab = "overview") {
    navigate(`/campaigns/${c.id}${tab && tab !== "overview" ? `?tab=${tab}` : ""}`);
  }
  function openSubmit(c) {
    navigate(`/campaigns/${c.id}?tab=submit`);
  }

  async function removeCampaign(id) {
    if (!isAdmin) {
      toast({ title: "Not allowed", description: "Only admins can delete campaigns." });
      return;
    }
    if (!window.confirm("Delete this campaign?")) return;
    setError(null);
    try {
      await api.delete(`/campaigns/${id}`);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function persistCampaignOrder(ordered) {
    try {
      await api.post("/admin/campaigns/reorder", {
        ordered_ids: ordered.map((x) => x.id),
      });
    } catch {
      // ignore
    }
  }

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((x) => x.id === active.id);
      const newIndex = prev.findIndex((x) => x.id === over.id);
      const next = arrayMove(prev, oldIndex, newIndex);
      persistCampaignOrder(next);
      return next;
    });
  }

  return (
    <div data-testid="campaigns-page" className="space-y-5">
      {/* Featured campaigns hero carousel */}
      {featuredItems.length > 0 ? (
        <FeaturedCampaignsCarousel
          items={featuredItems}
          onView={openDetails}
          onJoin={join}
          joinedIds={joinedIdSet}
        />
      ) : null}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div data-testid="campaigns-title" className="text-2xl font-semibold">
            Campaigns
          </div>
          <div data-testid="campaigns-subtitle" className="text-sm text-muted-foreground">
            Browse active campaigns, join, and start submitting clips to earn.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-[260px]">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="campaigns-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns..."
              className="rounded-full border-border/60 bg-background pl-8 pr-3 text-foreground"
            />
          </div>
          <div className="w-[190px]">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger
                data-testid="campaigns-status-filter"
                className="rounded-full border-border/60 bg-background text-foreground"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="campaigns-status-all" value="all">
                  All statuses
                </SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem data-testid={`campaigns-status-${s}`} key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-[190px]">
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger
                data-testid="campaigns-platform-filter"
                className="rounded-full border-border/60 bg-background text-foreground"
              >
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="campaigns-platform-all" value="all">


                  All platforms
                </SelectItem>
                {PLATFORMS.map((p) => (
                  <SelectItem data-testid={`campaigns-platform-${p}`} key={p} value={p}>
                    <span className="inline-flex items-center gap-2">
                      <img
                        alt={`${p} icon`}
                        src={PLATFORM_ICONS[p]}
                        className="h-4 w-4 rounded-sm"
                      />
                      {p}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {me?.role === "admin" || me?.role === "mod" ? (
            <Button data-testid="campaigns-create-button" className="rounded-full bg-[#c8ff00] text-black font-bold hover:bg-[#b8ef00] active:scale-[0.97]" onClick={() => navigate("/campaigns/new")}>Create Campaign</Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div
          data-testid="campaigns-error"
          className="rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300"
        >
          {error}
        </div>
      ) : null}

      {canManage ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={filteredItems.map((x) => x.id)} strategy={verticalListSortingStrategy}>
            <div className="campaign-card-grid">
              {filteredItems.map((c) => (
                <SortableCampaignCard key={c.id} campaign={c}>
                  <CampaignCardContent
                    c={c}
                    canManage={canManage}
                    isAdmin={isAdmin}
                    joinStatus={joinStatusById.get(c.id)}
                    isFav={favorites.has(c.id)}
                    onToggleFav={toggleFavorite}
                    onViewDetails={openDetails}
                    onOpenSubmit={openSubmit}
                    onJoin={join}
                    onEdit={(camp) => navigate(`/campaigns/${camp.id}/edit`)}
                    onRemove={removeCampaign}
                  />
                </SortableCampaignCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="campaign-card-grid">
          {filteredItems.map((c) => (
            <div key={c.id}>
              <CampaignCardContent
                c={c}
                canManage={canManage}
                isAdmin={isAdmin}
                joinStatus={joinStatusById.get(c.id)}
                isFav={favorites.has(c.id)}
                onToggleFav={toggleFavorite}
                onViewDetails={openDetails}
                onOpenSubmit={openSubmit}
                onJoin={join}
                onEdit={(camp) => navigate(`/campaigns/${camp.id}/edit`)}
                onRemove={removeCampaign}
              />
            </div>
          ))}
        </div>
      )}

      {!loading && search.trim() && filteredItems.length === 0 ? (
        <div
          data-testid="campaigns-no-search-results"
          className="rounded-2xl border border-dashed border-border/60 px-4 py-10 text-center text-sm text-muted-foreground"
        >
          No campaigns match <span className="font-semibold text-foreground">"{search}"</span>.
        </div>
      ) : null}

      {loading ? (
        <div className="campaign-card-grid" data-testid="campaigns-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-[18px] border border-border/60 bg-card/60">
              <div className="aspect-[16/11] w-full animate-pulse bg-zinc-200 dark:bg-zinc-800" />
              <div className="space-y-2.5 p-4">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
                  <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                </div>
                <div className="h-4 w-4/5 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="flex gap-2">
                  <div className="h-5 w-12 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                  <div className="h-5 w-14 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                  <div className="h-5 w-14 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                </div>
              </div>
              <div className="h-1.5 w-full animate-pulse bg-zinc-200 dark:bg-zinc-800" />
            </div>
          ))}
        </div>
      ) : !items.length ? (
        <div
          data-testid="campaigns-empty"
          className="rounded-2xl border border-border/60 bg-card/70 px-4 py-10 text-center text-sm text-muted-foreground"
        >
          No campaigns found.
        </div>
      ) : null}

      <CampaignDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        campaign={detailsCampaign}
        submissions={detailsSubmissions}
        onJoin={async (id) => { await join(id); }}
        initialTab={detailsInitialTab}
      />
    </div>
  );
}

export function CampaignEditorPage() {
  const { hasAccess } = useAuth();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(campaignId);
  const canManage = hasAccess(["campaign_management"], ["create_campaigns", "manage_campaigns"]);
  const [campaign, setCampaign] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/campaigns/${campaignId}`).then((r) => setCampaign(r.data)).catch((e) => setError(apiErrorMessage(e)));
  }, [campaignId, isEdit]);

  async function saveCampaign(payload) {
    setSaving(true); setError(null);
    try {
      const res = isEdit ? await api.put(`/campaigns/${campaignId}`, payload) : await api.post("/campaigns", payload);
      toast({ title: isEdit ? "Campaign updated" : "Campaign created" });
      navigate(`/campaigns/${res.data?.id || campaignId}`);
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setSaving(false); }
  }

  if (!canManage) return <div data-testid="campaign-editor-locked" className="rounded-2xl border border-border/60 bg-card/70 p-8 text-sm text-muted-foreground">You do not have access to create campaigns.</div>;
  if (isEdit && !campaign && !error) return <div data-testid="campaign-editor-loading" className="py-12 text-center text-sm text-muted-foreground">Loading campaign...</div>;

  return (
    <div data-testid="campaign-editor-page" className="mx-auto max-w-5xl space-y-5">
      <Button data-testid="campaign-editor-back" variant="ghost" className="gap-2 rounded-full text-zinc-400 hover:text-white" onClick={() => navigate("/campaigns")}><ArrowLeft size={14} /> Back to Campaigns</Button>
      <div><h1 data-testid="campaign-editor-title" className="page-title text-3xl">{isEdit ? "Edit Campaign" : "Create Campaign"}</h1><p className="page-subtitle">Build a full campaign page with categories, assets, and tutorial videos.</p></div>
      {error && <div data-testid="campaign-editor-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>}
      <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4 sm:p-6">
        <CampaignForm initial={campaign} saving={saving} onSave={saveCampaign} />
      </div>
    </div>
  );
}

export function CampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTabFromQuery = (() => {
    const q = new URLSearchParams(location.search).get("tab");
    // Map legacy values to new tab names for backwards-compat URLs
    const map = { overview: "details", your: "stats" };
    const resolved = map[q] || q;
    return ["details", "submit", "leaderboard", "stats"].includes(resolved) ? resolved : "details";
  })();
  const [campaign, setCampaign] = useState(null);
  const [progress, setProgress] = useState(null);
  const [joined, setJoined] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState(initialTabFromQuery);
  const [leaderboard, setLeaderboard] = useState([]);
  const [topClips, setTopClips] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [bulkRows, setBulkRows] = useState([{ video_url: "", platform: "", account_id: "", title: "" }]);
  const [subSuccess, setSubSuccess] = useState(null);

  async function load() {
    setError(null);
    try {
      const [c, p, j, s, a, lb, tc, ack, ann] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/campaigns/${campaignId}/progress`).catch(() => ({ data: null })),
        api.get("/campaigns/joined").catch(() => ({ data: [] })),
        api.get("/submissions", { params: { campaign_id: campaignId } }).catch(() => ({ data: [] })),
        api.get("/accounts").catch(() => ({ data: [] })),
        api.get(`/campaigns/${campaignId}/leaderboard`).catch(() => ({ data: { leaderboard: [] } })),
        api.get(`/campaigns/${campaignId}/top_clips`).catch(() => ({ data: { items: [] } })),
        api.get(`/campaigns/${campaignId}/acknowledge`).catch(() => ({ data: { acknowledged: false } })),
        api.get(`/campaigns/${campaignId}/announcements`).catch(() => ({ data: [] })),
      ]);
      setCampaign(c.data); setProgress(p.data); setJoined((j.data || []).some((x) => x.id === campaignId)); setSubmissions(s.data || []); setAccounts(a.data || []); setLeaderboard(lb.data?.leaderboard || []); setTopClips(tc.data?.items || []);
      setAcknowledged(!!ack.data?.acknowledged);
      setAnnouncements(Array.isArray(ann.data) ? ann.data : []);
    } catch (e) { setError(apiErrorMessage(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaignId]);

  // Re-fetch accounts whenever the user switches to the submit tab on the
  // campaign details page so newly added/removed socials are reflected.
  useEffect(() => {
    if (tab === "submit") {
      api.get("/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    }
  }, [tab]);

  async function joinCampaign() {
    try { await api.post(`/campaigns/${campaignId}/join`); setJoined(true); toast({ title: "Successfully joined" }); await load(); }
    catch (e) { setError(apiErrorMessage(e)); }
  }

  async function toggleAcknowledge(checked) {
    if (!campaignId || ackBusy || acknowledged || !checked) return;
    setAckBusy(true);
    try {
      await api.post(`/campaigns/${campaignId}/acknowledge`);
      setAcknowledged(true);
      toast({ title: "Acknowledged", description: "Submission unlocked." });
    } catch (e) {
      toast({ title: "Couldn't save", description: apiErrorMessage(e) });
    } finally { setAckBusy(false); }
  }

  function updateBulkRow(idx, patch) { setBulkRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r))); }
  function addBulkRow() { setBulkRows((rows) => (rows.length >= 3 ? rows : [...rows, { video_url: "", platform: "", account_id: "", title: "" }])); }
  function removeBulkRow(idx) { setBulkRows((rows) => (rows.length === 1 ? rows : rows.filter((_, i) => i !== idx))); }

  const validBulkRows = bulkRows.filter((r) => r.video_url && r.account_id);
  const canSubmit = joined && acknowledged && validBulkRows.length > 0 && !submitting;

  async function submitClips() {
    if (!canSubmit) return;
    setSubmitting(true); setError(null); setSubSuccess(null);
    try {
      const r = await api.post("/submissions/bulk", {
        campaign_id: campaignId,
        items: validBulkRows.map((row) => ({ video_url: row.video_url, platform: row.platform, account_id: row.account_id, title: row.title || "" })),
      });
      const count = Number(r.data?.count || validBulkRows.length);
      setSubSuccess(`${count} clip${count === 1 ? "" : "s"} submitted!`);
      setBulkRows([{ video_url: "", platform: "", account_id: "", title: "" }]);
      toast({ title: count === 1 ? "Clip submitted" : `${count} clips submitted` });
      await load();
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  const totalViews = submissions.reduce((a, s) => a + Number(s.views || 0), 0);
  const estRevenue = (totalViews / 1000) * Number(campaign?.payout_cpm || 0);
  const summaryTrend = useMemo(() => {
    const map = new Map();
    submissions.forEach((s) => {
      const day = String(s.created_at || "").slice(5, 10) || "Today";
      map.set(day, (map.get(day) || 0) + Number(s.views || 0));
    });
    return Array.from(map.entries()).map(([date, views]) => ({ date, views })).slice(-14);
  }, [submissions]);

  if (!campaign && !error) return <div data-testid="campaign-detail-loading" className="py-12 text-center text-sm text-muted-foreground">Loading campaign...</div>;

  async function copyLink() {
    try {
      const link = `${window.location.origin}/campaigns/${campaign?.id}`;
      await navigator.clipboard.writeText(link);
      toast({ title: "Campaign link copied", description: link });
    } catch { toast({ title: "Couldn't copy" }); }
  }

  return (
    <div data-testid="campaign-detail-page" className="mx-auto max-w-6xl space-y-5 px-3 sm:px-0">
      <Button data-testid="campaign-detail-back" variant="ghost" className="gap-2 rounded-full text-zinc-400 hover:text-white" onClick={() => navigate("/campaigns")}><ArrowLeft size={14} /> Campaigns</Button>
      {error && <div data-testid="campaign-detail-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>}
      {campaign && (
        <>
          <NewBannerCard campaign={campaign} joined={joined} onShare={copyLink} />

          <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
            {/* LEFT / MAIN COLUMN */}
            <div className="space-y-4 min-w-0">
              <NewDescriptionCard campaign={campaign} />
              <NewReferencesCard campaign={campaign} />
              <NewMusicBrandingCard campaign={campaign} />
              <NewRulesFooter />

              {/* Join button (mobile-prominent; desktop still shows in sidebar) */}
              {!joined && ["active", "available"].includes(campaign.status) && (
                <Button
                  data-testid="campaign-detail-join"
                  className="w-full rounded-full bg-[#c8ff00] py-6 font-bold text-black hover:bg-[#b8ef00]"
                  onClick={joinCampaign}
                >
                  Join Campaign
                </Button>
              )}

              {/* MOBILE-ONLY tabs (Details / Submit / Leaderboard / Stats) */}
              <div className="lg:hidden">
                <Tabs value={tab} onValueChange={setTab} className="w-full">
                  <NewTabsBar tab={tab} setTab={setTab} includeStats testIdPrefix="campaign-page" />

                  <TabsContent value="details" className="mt-3 space-y-3">
                    <DetailsTabContent campaign={campaign} progress={progress} />
                  </TabsContent>

                  <TabsContent value="submit" className="mt-3">
                    {!joined ? (
                      <div data-testid="campaign-submit-locked" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a]/80 p-10 text-center"><Lock className="mx-auto text-[#c8ff00]" /><div className="mt-3 font-bold text-white">Join to unlock submissions</div><Button className="mt-4 rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00]" onClick={joinCampaign}>Join Campaign</Button></div>
                    ) : (
                      <SubmitTabContent
                        campaign={campaign}
                        accounts={accounts}
                        bulkRows={bulkRows}
                        acknowledged={acknowledged}
                        ackBusy={ackBusy}
                        canSubmit={canSubmit}
                        submitting={submitting}
                        validBulkRows={validBulkRows}
                        error={error}
                        subSuccess={subSuccess}
                        toggleAcknowledge={toggleAcknowledge}
                        updateBulkRow={updateBulkRow}
                        addBulkRow={addBulkRow}
                        removeBulkRow={removeBulkRow}
                        submitClips={submitClips}
                        idPrefix="campaign-page"
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="leaderboard" className="mt-3">
                    <NewLeaderboardList leaderboard={leaderboard} />
                  </TabsContent>

                  <TabsContent value="stats" className="mt-3">
                    <NewStatsPanel
                      campaign={campaign}
                      progress={progress}
                      totalViews={totalViews}
                      totalSubmissions={submissions.length}
                      summaryTrend={summaryTrend}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            {/* RIGHT / SIDEBAR (DESKTOP ONLY) */}
            <aside data-testid="campaign-detail-sidebar" className="hidden lg:block">
              <div className="sticky top-4 space-y-4">
                <NewStatsPanel
                  campaign={campaign}
                  progress={progress}
                  totalViews={totalViews}
                  totalSubmissions={submissions.length}
                  summaryTrend={summaryTrend}
                />

                <Tabs value={tab === "stats" ? "details" : tab} onValueChange={setTab} className="w-full">
                  <NewTabsBar tab={tab} setTab={setTab} includeStats={false} testIdPrefix="campaign-desktop" />

                  <TabsContent value="details" className="mt-3 space-y-3">
                    <DetailsTabContent campaign={campaign} progress={progress} />
                  </TabsContent>

                  <TabsContent value="submit" className="mt-3">
                    {!joined ? (
                      <div data-testid="campaign-desktop-submit-locked" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a]/80 p-10 text-center"><Lock className="mx-auto text-[#c8ff00]" /><div className="mt-3 font-bold text-white">Join to unlock submissions</div><Button className="mt-4 rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00]" onClick={joinCampaign}>Join Campaign</Button></div>
                    ) : (
                      <SubmitTabContent
                        campaign={campaign}
                        accounts={accounts}
                        bulkRows={bulkRows}
                        acknowledged={acknowledged}
                        ackBusy={ackBusy}
                        canSubmit={canSubmit}
                        submitting={submitting}
                        validBulkRows={validBulkRows}
                        error={error}
                        subSuccess={subSuccess}
                        toggleAcknowledge={toggleAcknowledge}
                        updateBulkRow={updateBulkRow}
                        addBulkRow={addBulkRow}
                        removeBulkRow={removeBulkRow}
                        submitClips={submitClips}
                        idPrefix="campaign-desktop"
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="leaderboard" className="mt-3">
                    <NewLeaderboardList leaderboard={leaderboard} />
                  </TabsContent>
                </Tabs>
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}


function DetailsTabContent({ campaign, progress }) {
  return (
    <div className="space-y-3">
      <div data-testid="details-tab-stats" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <DetailStat label="Min Views" value={fmtNumber(campaign.min_views || 1000)} />
        <DetailStat label="Pay / 1K" value={`$${Number(campaign.payout_cpm || 0).toFixed(2)}`} />
        <DetailStat label="Max / Video" value={`$${Number(campaign.max_payout_per_video || 75).toFixed(0)}`} />
        <DetailStat label="Users Joined" value={fmtNumber(campaign.joined_count || 0)} />
        <DetailStat label="Total Views" value={fmtNumber(progress?.accepted_views || 0)} />
        <DetailStat label="Type" value={campaign.campaign_type || "Clipping"} />
      </div>

      <div data-testid="details-tab-platforms" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Platforms</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(campaign.platforms_allowed || campaign.platforms || []).map((p) => (
            <span key={p} className="inline-flex items-center gap-1.5 rounded-full border border-[#1a1a1a] bg-black px-3 py-1.5 text-[12px] font-semibold capitalize text-white">
              <PlatformIcon platform={String(p).toLowerCase()} size={12} /> {p}
            </span>
          ))}
          {(!campaign.platforms_allowed || campaign.platforms_allowed.length === 0) && (!campaign.platforms || campaign.platforms.length === 0) ? (
            <span className="text-[12px] text-zinc-500">All platforms</span>
          ) : null}
        </div>
      </div>

      {(campaign.payment_methods_allowed || []).length > 0 && (
        <div data-testid="details-tab-payments" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Payment Methods</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(campaign.payment_methods_allowed || []).map((m) => {
              const label = (PAYMENT_METHODS.find((x) => x.key === m)?.label) || m;
              return (
                <span key={m} className="inline-flex items-center gap-1.5 rounded-full border border-[#1a1a1a] bg-black/40 px-2.5 py-1 text-[11px] font-semibold capitalize text-white">
                  <PaymentMethodIcon method={m} size={12} /> {label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {(campaign.start_date || campaign.end_date) && (
        <div data-testid="details-tab-schedule" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Schedule</div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div className="flex items-center justify-between rounded-xl border border-[#1a1a1a] bg-black px-3 py-2">
              <span className="text-[12px] font-semibold text-white">Start</span>
              <span className="text-[12px] font-black text-white">{campaign.start_date ? new Date(campaign.start_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</span>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#1a1a1a] bg-black px-3 py-2">
              <span className="text-[12px] font-semibold text-white">End</span>
              <span className="text-[12px] font-black text-white">{campaign.end_date ? new Date(campaign.end_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmitTabContent({
  campaign,
  accounts,
  bulkRows,
  acknowledged,
  ackBusy,
  canSubmit,
  submitting,
  validBulkRows,
  error,
  subSuccess,
  toggleAcknowledge,
  updateBulkRow,
  addBulkRow,
  removeBulkRow,
  submitClips,
  idPrefix,
}) {
  return (
    <div className="space-y-3">
      {error && <div data-testid={`${idPrefix}-submit-error`} className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</div>}
      {subSuccess && <div data-testid={`${idPrefix}-submit-success`} className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">{subSuccess}</div>}

      <label data-testid={`${idPrefix}-ack-toggle`} className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-3 transition-colors ${acknowledged ? "border-[#c8ff00]/40 bg-[#c8ff00]/[0.06]" : "border-amber-500/30 bg-amber-500/[0.06]"}`}>
        <Checkbox data-testid={`${idPrefix}-ack-checkbox`} checked={acknowledged} disabled={acknowledged || ackBusy} onCheckedChange={(v) => toggleAcknowledge(!!v)} className="mt-0.5 border-[#c8ff00]/60 data-[state=checked]:bg-[#c8ff00] data-[state=checked]:text-black" />
        <div className="text-xs">
          <div className={`font-semibold ${acknowledged ? "text-[#c8ff00]" : "text-amber-300"}`}>I have read and understood the campaign details</div>
          <div className="mt-0.5 text-zinc-400">{acknowledged ? "Submission is unlocked. You can now post up to 3 clips at once." : "Toggle this to unlock clip submission for this campaign."}</div>
        </div>
      </label>

      <div className="space-y-2.5">
        {bulkRows.map((row, idx) => (
          <div key={idx} data-testid={`${idPrefix}-bulk-row-${idx}`} className={`rounded-2xl border p-3 transition-opacity ${acknowledged ? "border-[#1a1a1a] bg-[#0a0a0a]" : "pointer-events-none border-[#1a1a1a]/60 bg-[#0a0a0a]/60 opacity-50"}`}>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Clip #{idx + 1}</div>
              {bulkRows.length > 1 && (
                <button type="button" data-testid={`${idPrefix}-bulk-remove-${idx}`} onClick={() => removeBulkRow(idx)} className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#1a1a1a] text-zinc-500 hover:border-rose-500/50 hover:text-rose-400" aria-label={`Remove clip ${idx + 1}`}><XIcon size={12} /></button>
              )}
            </div>
            <div className="space-y-2">
              <Input data-testid={`${idPrefix}-bulk-url-${idx}`} value={row.video_url} onChange={(e) => updateBulkRow(idx, { video_url: e.target.value })} placeholder="Paste TikTok / IG / YouTube link" disabled={!acknowledged} className="border-[#1a1a1a] bg-black text-white" />
              {(() => {
                const allowed = campaign.platforms_allowed && campaign.platforms_allowed.length ? campaign.platforms_allowed : null;
                const filteredAccounts = accounts.filter((a) => !allowed || allowed.includes(a.platform));
                return (
                  <Select
                    value={row.account_id}
                    onValueChange={(v) => {
                      const acc = accounts.find((a) => a.id === v);
                      updateBulkRow(idx, { account_id: v, platform: acc?.platform || "" });
                    }}
                    disabled={!acknowledged || filteredAccounts.length === 0}
                  >
                    <SelectTrigger data-testid={`${idPrefix}-bulk-account-${idx}`} className="border-[#1a1a1a] bg-black text-white">
                      <SelectValue placeholder={filteredAccounts.length === 0 ? `Add a ${(allowed || ["matching"]).join("/")} account in Socials` : "Choose your account"} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredAccounts.length === 0 ? (
                        <div data-testid={`${idPrefix}-bulk-account-empty-${idx}`} className="px-2 py-3 text-center text-xs text-zinc-500">Add a {(allowed || ["matching"]).join("/")} account in Socials</div>
                      ) : filteredAccounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          <span className="inline-flex items-center gap-2"><PlatformIcon platform={a.platform} size={14} /> {a.handle} <span className="text-[10px] uppercase text-zinc-500">· {a.platform}</span></span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
          </div>
        ))}
        {bulkRows.length < 3 && (
          <button type="button" data-testid={`${idPrefix}-bulk-add`} onClick={addBulkRow} disabled={!acknowledged} className="inline-flex w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed border-[#c8ff00]/40 bg-transparent px-4 py-2.5 text-xs font-semibold text-[#c8ff00] transition-colors hover:bg-[#c8ff00]/[0.06] disabled:cursor-not-allowed disabled:opacity-40"><Plus size={14} /> Add another clip ({bulkRows.length}/3)</button>
        )}
        <Button data-testid={`${idPrefix}-submit-button`} disabled={!canSubmit} onClick={submitClips} className="w-full rounded-xl bg-[#c8ff00] py-5 font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97] disabled:opacity-50">{submitting ? "Submitting..." : !acknowledged ? "Read & confirm to unlock submit" : validBulkRows.length > 1 ? `Submit ${validBulkRows.length} clips` : "Submit Clip"}</Button>
        <div className="text-[11px] text-zinc-500">You can submit up to 3 videos at once · Account must be verified</div>
      </div>
    </div>
  );
}



function SortableCampaignCard({ campaign, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: campaign.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? "opacity-60" : "opacity-100"}>
      <div className="relative">
        <div
          data-testid={`campaign-drag-handle-${campaign.id}`}
          aria-label="Drag to reorder"
          title="Drag to reorder"
          className="absolute right-3 top-12 z-10 cursor-grab select-none rounded-full border border-border/60 bg-background/80 p-1.5 text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={14} />
        </div>
        {children}
      </div>
    </div>
  );
}


function DetailStat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-border/60 bg-background p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold text-foreground">{value}</div>
    </div>
  );
}

function videoEmbedUrl(url = "") {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { provider: "youtube", url: `https://www.youtube.com/embed/${yt[1]}` };
  const tt = url.match(/tiktok\.com\/(?:@[^/]+\/video\/|v\/)(\d+)/);
  if (tt) return { provider: "tiktok", url: `https://www.tiktok.com/embed/v2/${tt[1]}` };
  return null;
}

function ClipPreviewCard({ clip }) {
  const embed = videoEmbedUrl(clip.video_url || "");
  return (
    <div data-testid={`top-performing-clip-${clip.rank}`} className="overflow-hidden rounded-2xl border border-[#1a1a1a] bg-black">
      <div className="aspect-video bg-black">
        {embed ? (
          <iframe
            data-testid={`top-clip-embed-${clip.rank}`}
            title={`Top clip ${clip.rank}`}
            src={embed.url}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <a href={clip.video_url} target="_blank" rel="noreferrer" className="flex h-full w-full items-center justify-center gap-2 text-sm font-bold text-[#c8ff00]">
            <PlayCircle size={20} /> Watch Clip
          </a>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
        <div className="min-w-0">
          <div className="font-bold text-white">#{clip.rank} @{clip.username}</div>
          <div className="text-zinc-500">Accepted only</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="shrink-0 rounded-full bg-[#c8ff00]/10 px-2 py-1 font-black text-[#c8ff00]">{compactNumber(clip.views)} views</div>
          <a data-testid={`top-clip-open-${clip.rank}`} href={clip.video_url} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-[#c8ff00] hover:text-[#d8ff33]">
            Open Clip
          </a>
        </div>
      </div>
    </div>
  );
}

function SubmissionRow({ s, onAppealed }) {
  const [appealOpen, setAppealOpen] = useState(false);
  const isRejected = s.status === "flagged" || s.status === "rejected";
  const appealStatus = s.appeal_status; // "pending" | "approved" | "denied"
  return (
    <div
      data-testid={`campaign-details-sub-${s.id}`}
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-background p-3"
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-foreground">{s.platform || "—"}</div>
        <a
          className="mt-1 block truncate text-xs text-cyan-600 underline underline-offset-4 dark:text-cyan-700 dark:text-cyan-300"
          href={s.video_url}
          target="_blank"
          rel="noreferrer"
        >
          <span className="inline-flex items-center gap-1"><ExternalLink size={12} /> Open Clip</span>
        </a>
        {isRejected && (s.rejection_reason || s.reason) && (
          <div data-testid={`sub-rejection-reason-${s.id}`} className="mt-1 max-w-[420px] truncate text-[11px] text-rose-300">
            Reason: {s.rejection_reason || s.reason}
          </div>
        )}
        {!isRejected && s.status === "pending" && (s.status_reason || s.upload_age_note) && (
          <div data-testid={`sub-status-reason-${s.id}`} className="mt-1 max-w-[420px] text-[11px] text-amber-500 dark:text-amber-300">
            {s.status_reason || s.upload_age_note}
          </div>
        )}
        {s.views_flagged && (
          <div data-testid={`sub-views-flagged-${s.id}`} className="mt-1 max-w-[420px] text-[11px] text-amber-500 dark:text-amber-300">
            Unusual view spike detected — pending verification before it counts.
          </div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">
          Last synced: {s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : "—"}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="bg-muted text-foreground hover:bg-muted/80">Views: {s.views || 0}</Badge>
        {s.views_flagged && (
          <Badge data-testid={`sub-verifying-badge-${s.id}`} className="bg-amber-500/15 text-amber-300">Verifying</Badge>
        )}
        {appealStatus === "pending" ? (
          <Badge data-testid={`sub-appeal-pending-${s.id}`} className="bg-amber-500/15 text-amber-300">Appeal Pending</Badge>
        ) : (
          <Badge className="bg-muted text-foreground hover:bg-muted/80 capitalize">{s.status}</Badge>
        )}
        {isRejected && !appealStatus && (
          <Button
            data-testid={`sub-appeal-btn-${s.id}`}
            size="sm"
            variant="secondary"
            className="rounded-full bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
            onClick={() => setAppealOpen(true)}
          >
            Appeal
          </Button>
        )}
      </div>
      <AppealDialog
        open={appealOpen}
        onOpenChange={setAppealOpen}
        submission={s}
        onSubmitted={onAppealed}
      />
    </div>
  );
}

export function CampaignDetailsDialog({ open, onOpenChange, campaign, submissions, onJoin, initialTab = "details" }) {
  // Backward-compat: map legacy tab names to new ones
  const _legacyMap = { overview: "details", your: "stats" };
  const _resolveTab = (t) => {
    const m = _legacyMap[t] || t;
    return ["details", "submit", "leaderboard", "stats"].includes(m) ? m : "details";
  };
  const [tab, setTab] = useState(_resolveTab(initialTab));
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [subSuccess, setSubSuccess] = useState(null);
  const [subError, setSubError] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbPeriod, setLbPeriod] = useState("all");
  const [lbLoading, setLbLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [bulkRows, setBulkRows] = useState([{ video_url: "", platform: "", account_id: "", title: "" }]);

  async function loadLeaderboard(period) {
    if (!campaign?.id) return;
    setLbLoading(true);
    try {
      const r = await api.get(`/campaigns/${campaign.id}/leaderboard`, { params: { period } });
      setLeaderboard(r.data?.leaderboard || []);
    } catch { setLeaderboard([]); }
    finally { setLbLoading(false); }
  }

  useEffect(() => {
    if (open && campaign?.id) {
      setTab(_resolveTab(initialTab));
      setSubSuccess(null);
      setSubError(null);
      setBulkRows([{ video_url: "", platform: "", account_id: "", title: "" }]);
      setLeaderboard([]);
      setLbPeriod("all");
      api.get("/accounts").then((r) => setAccounts(r.data || [])).catch(() => setAccounts([]));
      api.get(`/campaigns/${campaign.id}/acknowledge`)
        .then((r) => setAcknowledged(!!r.data?.acknowledged))
        .catch(() => setAcknowledged(false));
      api.get(`/campaigns/${campaign.id}/announcements`)
        .then((r) => setAnnouncements(Array.isArray(r.data) ? r.data : []))
        .catch(() => setAnnouncements([]));
    }
  }, [open, campaign?.id, initialTab]);

  useEffect(() => {
    if (open && tab === "leaderboard") loadLeaderboard(lbPeriod);
  }, [open, tab, lbPeriod]);

  // Re-fetch accounts whenever the user opens the submit tab so additions/
  // deletions made on the Socials page are reflected without closing the dialog.
  useEffect(() => {
    if (open && tab === "submit") {
      api.get("/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    }
  }, [open, tab]);

  async function toggleAcknowledge(checked) {
    if (!campaign?.id || ackBusy) return;
    if (acknowledged) return; // one-way; cannot un-acknowledge
    if (!checked) return;
    setAckBusy(true);
    try {
      await api.post(`/campaigns/${campaign.id}/acknowledge`);
      setAcknowledged(true);
      toast({ title: "Acknowledged", description: "You can now submit clips for this campaign." });
    } catch (e) {
      toast({ title: "Couldn't save", description: apiErrorMessage(e) });
    } finally {
      setAckBusy(false);
    }
  }

  function updateBulkRow(idx, patch) {
    setBulkRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function addBulkRow() {
    setBulkRows((rows) => (rows.length >= 3 ? rows : [...rows, { video_url: "", platform: "", account_id: "", title: "" }]));
  }
  function removeBulkRow(idx) {
    setBulkRows((rows) => (rows.length === 1 ? rows : rows.filter((_, i) => i !== idx)));
  }

  const totalViews = (submissions || []).reduce((a, s) => a + Number(s.views || 0), 0);
  const payout = Number(campaign?.payout_cpm || 0);
  const estRevenue = (totalViews / 1000) * payout;
  const validBulkRows = bulkRows.filter((r) => r.video_url && r.account_id);
  const canSubmit = acknowledged && validBulkRows.length > 0 && !submitting;

  async function submitClips() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubError(null);
    setSubSuccess(null);
    try {
      const r = await api.post("/submissions/bulk", {
        campaign_id: campaign?.id,
        items: validBulkRows.map((row) => ({
          video_url: row.video_url,
          platform: row.platform,
          account_id: row.account_id,
          title: row.title || "",
        })),
      });
      const count = Number(r.data?.count || validBulkRows.length);
      setSubSuccess(`${count} clip${count === 1 ? "" : "s"} submitted!`);
      setBulkRows([{ video_url: "", platform: "", account_id: "", title: "" }]);
    } catch (e) {
      setSubError(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  const summaryTrend = useMemo(() => {
    const map = new Map();
    (submissions || []).forEach((s) => {
      const day = String(s.created_at || "").slice(5, 10) || "Today";
      map.set(day, (map.get(day) || 0) + Number(s.views || 0));
    });
    return Array.from(map.entries()).map(([date, views]) => ({ date, views })).slice(-14);
  }, [submissions]);

  async function copyLink() {
    try {
      const link = `${window.location.origin}/campaigns/${campaign?.id}`;
      await navigator.clipboard.writeText(link);
      toast({ title: "Campaign link copied", description: link });
    } catch { toast({ title: "Couldn't copy" }); }
  }

  const joined = true; // dialog is only opened from a joined campaign context typically; gate retained via onJoin button
  const canJoin = campaign && ["active", "available"].includes(campaign?.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="campaign-details-dialog"
        className="w-[98vw] max-w-[1200px] max-h-[94vh] overflow-y-auto border-[#1a1a1a] bg-[#0a0a0a] text-foreground sm:w-full"
      >
        <DialogHeader className="sr-only">
          <DialogTitle data-testid="campaign-details-title">{campaign?.name || "Campaign"}</DialogTitle>
        </DialogHeader>

        {campaign && (
          <div className="space-y-4">
            <NewBannerCard campaign={campaign} onShare={copyLink} onClose={() => onOpenChange?.(false)} />

            <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
              {/* LEFT / MAIN COLUMN */}
              <div className="space-y-3 min-w-0">
                <NewDescriptionCard campaign={campaign} />
                <NewReferencesCard campaign={campaign} />
                <NewMusicBrandingCard campaign={campaign} />
                <NewRulesFooter />

                {canJoin && (
                  <Button
                    data-testid="campaign-details-join"
                    className="w-full rounded-full bg-[#c8ff00] py-5 font-bold text-black hover:bg-[#b8ef00]"
                    onClick={() => onJoin?.(campaign.id)}
                  >
                    Join Campaign
                  </Button>
                )}

                {/* MOBILE-ONLY tabs */}
                <div className="lg:hidden">
                  <Tabs value={tab} onValueChange={setTab} className="w-full">
                    <NewTabsBar tab={tab} setTab={setTab} includeStats testIdPrefix="campaign-dialog" />

                    <TabsContent value="details" className="mt-3 space-y-3">
                      <DetailsTabContent campaign={campaign} progress={null} />
                    </TabsContent>

                    <TabsContent value="submit" className="mt-3">
                      <SubmitTabContent
                        campaign={campaign}
                        accounts={accounts}
                        bulkRows={bulkRows}
                        acknowledged={acknowledged}
                        ackBusy={ackBusy}
                        canSubmit={canSubmit}
                        submitting={submitting}
                        validBulkRows={validBulkRows}
                        error={subError}
                        subSuccess={subSuccess}
                        toggleAcknowledge={toggleAcknowledge}
                        updateBulkRow={updateBulkRow}
                        addBulkRow={addBulkRow}
                        removeBulkRow={removeBulkRow}
                        submitClips={submitClips}
                        idPrefix="campaign-dialog"
                      />
                    </TabsContent>

                    <TabsContent value="leaderboard" className="mt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">Top Clippers</div>
                        <Select value={lbPeriod} onValueChange={setLbPeriod}>
                          <SelectTrigger data-testid="leaderboard-period" className="h-8 w-[120px] border-[#1a1a1a] bg-black text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All time</SelectItem>
                            <SelectItem value="weekly">This week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {lbLoading ? (
                        <div className="py-8 text-center text-sm text-zinc-500">Loading...</div>
                      ) : (
                        <NewLeaderboardList leaderboard={leaderboard} />
                      )}
                    </TabsContent>

                    <TabsContent value="stats" className="mt-3">
                      <NewStatsPanel
                        campaign={campaign}
                        progress={null}
                        totalViews={totalViews}
                        totalSubmissions={(submissions || []).length}
                        summaryTrend={summaryTrend}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </div>

              {/* RIGHT / SIDEBAR (DESKTOP ONLY) */}
              <aside data-testid="campaign-dialog-sidebar" className="hidden lg:block">
                <div className="space-y-3">
                  <NewStatsPanel
                    campaign={campaign}
                    progress={null}
                    totalViews={totalViews}
                    totalSubmissions={(submissions || []).length}
                    summaryTrend={summaryTrend}
                  />

                  <Tabs value={tab === "stats" ? "details" : tab} onValueChange={setTab} className="w-full">
                    <NewTabsBar tab={tab} setTab={setTab} includeStats={false} testIdPrefix="campaign-dialog-desktop" />

                    <TabsContent value="details" className="mt-3 space-y-3">
                      <DetailsTabContent campaign={campaign} progress={null} />
                    </TabsContent>

                    <TabsContent value="submit" className="mt-3">
                      <SubmitTabContent
                        campaign={campaign}
                        accounts={accounts}
                        bulkRows={bulkRows}
                        acknowledged={acknowledged}
                        ackBusy={ackBusy}
                        canSubmit={canSubmit}
                        submitting={submitting}
                        validBulkRows={validBulkRows}
                        error={subError}
                        subSuccess={subSuccess}
                        toggleAcknowledge={toggleAcknowledge}
                        updateBulkRow={updateBulkRow}
                        addBulkRow={addBulkRow}
                        removeBulkRow={removeBulkRow}
                        submitClips={submitClips}
                        idPrefix="campaign-dialog-desktop"
                      />
                    </TabsContent>

                    <TabsContent value="leaderboard" className="mt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">Top Clippers</div>
                        <Select value={lbPeriod} onValueChange={setLbPeriod}>
                          <SelectTrigger className="h-8 w-[120px] border-[#1a1a1a] bg-black text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All time</SelectItem>
                            <SelectItem value="weekly">This week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {lbLoading ? (
                        <div className="py-8 text-center text-sm text-zinc-500">Loading...</div>
                      ) : (
                        <NewLeaderboardList leaderboard={leaderboard} />
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
              </aside>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
